import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'
import { TableList, TableMemoryPagination } from '@/services/table-memory-pagination'
import {
  BaselineScanSettings,
  EarlyWarningAndMonitoringSettings,
  FlowControlSettings, LeakFixSettings, OnlineBlackAndWhiteListSettings, RegularPhysicalExaminationSettings,
  TimingAntivirusSettings, USBManageSettings
} from '@/views/dashboard/_components/group-policy-settings/group-policy-settings.service'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import { getAreaByTerminalType } from '@/services/service-permission.service'
import { i18n } from '@/i18n'

export interface FetchOptions {
  group_id: number // 分组id
  login_status: number // 装态
  system_type: number
  key_word: string // 关键词搜索
  page: number // 页码
  limit: number // 每页数量
  sort: string // 排序字段名
  asc: boolean // true-正序，false-逆序
  useMemory: boolean // 是否使用内存数据
  system_types?: number[]
  permission_names?: string[]
}

export interface DeleteOptions {
  device_id: string, // 终端id
  is_black: boolean // 是否拉黑
}
export interface MoveGroupOptions {
  type: number, // 迁移类型，1-指定终端id，2-指定分组
  new_group_id: number, // 转移的新分组id
  device_list: string[], // 终端ID集合
  origin_group_id: number // 源分组id
}
export interface BlacklistOptions {
  group_id: number // 分组id
  page: number // 页码
  limit: number // 每页数量
}

export interface Info {
  regular_exam: RegularPhysicalExaminationSettings // 定时体检
  regular_scan: TimingAntivirusSettings // 定时查杀
  flow_control: FlowControlSettings // 流量监控
  net_black_white: OnlineBlackAndWhiteListSettings // 网路黑白名单
  early_warning: EarlyWarningAndMonitoringSettings // 预警监控
  leak_repair: LeakFixSettings // 漏洞修复
  peripheral_control: USBManageSettings // 外设管控
  baseline_check: BaselineScanSettings // 基线检测
  leak_repair_linux: LeakFixSettings // linux漏洞修复
  regular_anti_virus_linux: TimingAntivirusSettings // linux定时查杀
}
export interface ClientStrategyRes {
  info: Info,
  strategy_name: string
  strategy_id: string
}
export interface OrderOptions {
  execute_type: number,
  device_list: string[],
  group_id: number
}
export interface ExtraOptions {
  execute_type: number,
  device_list: string[],
  group_id: number,
  message: string
}

export interface TerminalExecParams {
  execute_type: 0 | 1
  device_list: string[]
  group_id: number
  message: string
}

export interface ExecuteTerminalActionOptions {
  serviceMethod: (params: any) => Promise<any>
  actionName: string
  paramsOverride?: any
  onSuccess?: () => void
}
export interface OsVersionList {
  /** 系统类型 1-windows, 2-linux, 3-windows server, 4-mac */
  system_type: number
  /** 系统类型名称 */
  system_name: string
  /** 终端数量 */
  count: number
}

export interface distributionResponseData {
  /** 系统版本分布列表 */
  os_version_list: OsVersionList[]
}
export interface onlineRequestParams {
  /** 开始时间（Unix时间戳，秒） */
  start_time: string
  /** 结束时间（Unix时间戳，秒） */
  end_time: string
  /** 时间粒度 */
  granularity: string
}

export interface onlineResponseData {
  /** 离线数量列表 */
  offline_counts?: number[]
  /** 在线数量列表 */
  online_counts?: number[]
  /** 时间点列表（Unix时间戳，秒） */
  time_points?: string[]
}
@Injectable()
export class ClientService extends TableMemoryPagination<FetchOptions, ClientItem> {
  constructor (private http: Http) {
    super()
  }

  isEqual (left: ClientItem, right: ClientItem): boolean {
    return left.device_id === right.device_id
  }

  @ParamsClean()
  @InvokeThrottle()
  getListByServer (options: FetchOptions): Promise<TableList<ClientItem>> {
    return this.http
      .post<{
        total: number,
        enable_total: number,
        terminal_count: number,
        terminal_list: ClientItem[]
      }>('/terminal/list', options).then(res => {
        res.terminal_list.forEach(v => v.area_type = getAreaByTerminalType(v.system_type))
        return {
          ...res,
          list: res.terminal_list,
        }
      })
  }

  download (type: 0|1, options: FetchOptions, moduleName: string) {
    const params = Object.assign({}, options, {
      file_type: type,
      page: 0, // 不分页
      limit: 0
    })
    return this.http.downloadWithBlob('/terminal/list/export', params, moduleName)
  }

  deleteTerminal (options: DeleteOptions) {
    return this.http
      .post('/terminal/delete', options)
  }

  fetchBlackList (options: BlacklistOptions) {
    return this.http
      .post<{
        total: number,
        black_list: [{
          device_id: string,
          computer_name: string
        }]
      }>('/terminal/black-list/get', options)
  }

  cancelBlackList (options: { device_id: string }) {
    return this.http
      .post('/terminal/black/cancel', options)
  }

  moveTerminal (options: MoveGroupOptions) {
    return this.http
      .post('/terminal/transfer', options)
  }

  fetchClientStrategy (options: { device_id: string }) {
    return this.http
      .post<ClientStrategyRes>('/terminal-strategy/get', options)
  }

  @InvokeThrottle()
  shutDownTerminal (options: OrderOptions) {
    return this.http
      .post('/terminal-order/shutdown', options)
  }

  @InvokeThrottle()
  restartTerminal (options: OrderOptions) {
    return this.http
      .post('/terminal-order/restart', options)
  }

  @InvokeThrottle()
  upgradeTerminal (options: OrderOptions) {
    return this.http.post<{ task_id: string }>('/terminal-order/client-upgrade', options)
  }

  @InvokeThrottle()
  completeInfo (options: ExtraOptions) {
    options.message =  i18n.global.t('groupClient.terminalActionMessageInfo', { message: options.message })
    return this.http
      .post('/terminal-order/complete-info', options)
  }

  updateTerminalName (options: { new_name: string, device_id: string,team_id?:string }) {
    return this.http
      .post('/terminal/modify_name', options)
  }
  updateTerminalRemark (options: { remark: string, device_id: string,team_id?:string }) {
    return this.http
      .post('/terminal/modify_remark', options)
  }
  sendMessage (params: Partial<TerminalExecParams>) {
    return this.http.post<{
      task_id: string
    }>('/terminal-order/message-send', params)
  }
  // 系统版本分布TOP10
  getTerminalOsDistribution () {
    return this.http.post<distributionResponseData>('/terminal/os-distribution', {})
  }
  // 在线/离线趋势
  getTerminalOnlineTrend (data: onlineRequestParams) {
    return this.http.post<onlineResponseData>('/terminal/online-trend', data)
  }
}
